import React, { useState } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox,
  Chip,
  Grid,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  FormHelperText,
  Divider,
  Stack
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  AccountTree as NetworkIcon,
  Route as SectionIcon,
  Remove as RemoveIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';

const NetworkSectionSelector = ({ 
  assignableResources, 
  selectedPermissions, 
  onChange, 
  error 
}) => {
  const [expandedNetworks, setExpandedNetworks] = useState(new Set());

  const handleNetworkExpand = (networkId) => {
    const newExpanded = new Set(expandedNetworks);
    if (newExpanded.has(networkId)) {
      newExpanded.delete(networkId);
    } else {
      newExpanded.add(networkId);
    }
    setExpandedNetworks(newExpanded);
  };

  const isNetworkSelected = (networkId) => {
    return selectedPermissions.some(p => p.type === 'network' && p.id === networkId);
  };

  const isSectionSelected = (sectionId) => {
    return selectedPermissions.some(p => p.type === 'section' && p.id === sectionId);
  };

  const getNetworkSectionCount = (networkId) => {
    const network = assignableResources?.networks?.find(n => n.id === networkId);
    return network?.sections?.length || 0;
  };

  const getSelectedSectionsInNetwork = (networkId) => {
    const network = assignableResources?.networks?.find(n => n.id === networkId);
    if (!network) return 0;
    
    return network.sections.filter(section => 
      isSectionSelected(section.id)
    ).length;
  };

  const handleNetworkToggle = (network) => {
    const isSelected = isNetworkSelected(network.id);
    let newPermissions = [...selectedPermissions];

    if (isSelected) {
      // Remove network and all its sections
      newPermissions = newPermissions.filter(p => 
        !(p.type === 'network' && p.id === network.id) &&
        !(p.type === 'section' && network.sections?.some(s => s.id === p.id))
      );
    } else {
      // Add network (this gives access to all current and future sections)
      newPermissions.push({
        id: network.id,
        type: 'network',
        name: network.name,
        description: network.description
      });

      // Remove any individual sections from this network (network access supersedes)
      newPermissions = newPermissions.filter(p => 
        !(p.type === 'section' && network.sections?.some(s => s.id === p.id))
      );
    }

    onChange(newPermissions);
  };

  const handleSectionToggle = (section, networkId) => {
    const isSelected = isSectionSelected(section.id);
    const isNetworkSelected = selectedPermissions.some(p => p.type === 'network' && p.id === networkId);
    
    if (isNetworkSelected) {
      // Can't select individual sections if network is selected
      return;
    }

    let newPermissions = [...selectedPermissions];

    if (isSelected) {
      // Remove section
      newPermissions = newPermissions.filter(p => 
        !(p.type === 'section' && p.id === section.id)
      );
    } else {
      // Add section
      newPermissions.push({
        id: section.id,
        type: 'section',
        name: section.name,
        description: section.description || '',
        networkId: networkId
      });
    }

    onChange(newPermissions);
  };

  const removePermission = (permission) => {
    const newPermissions = selectedPermissions.filter(p => 
      !(p.type === permission.type && p.id === permission.id)
    );
    onChange(newPermissions);
  };

  if (!assignableResources) {
    return (
      <Alert severity="info">
        Loading available networks and sections...
      </Alert>
    );
  }

  if (!assignableResources.networks || assignableResources.networks.length === 0) {
    return (
      <Alert severity="warning">
        No networks available for assignment. Please contact your administrator.
      </Alert>
    );
  }

  return (
    <Stack spacing={3}>
      <Typography variant="h6" fontWeight={600}>
        Select Networks and Sections
      </Typography>
      
      <Alert severity="info">
        <Typography variant="body2">
          <strong>Network Access:</strong> Grants access to ALL sections within the network (current and future).
          <br />
          <strong>Section Access:</strong> Grants access only to specific sections within a network.
        </Typography>
      </Alert>

      {/* Selection Area */}
      <Grid container spacing={3}>
        {/* Left Panel - Available Resources */}
        <Grid item xs={12} md={8}>
          <Paper variant="outlined" sx={{ maxHeight: 400, overflow: 'auto' }}>
            <Box sx={{ p: 3 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Available Networks & Sections
              </Typography>
              
              <Stack spacing={1}>
                {assignableResources.networks.map((network) => (
                  <Accordion 
                    key={network.id}
                    expanded={expandedNetworks.has(network.id)}
                    onChange={() => handleNetworkExpand(network.id)}
                    elevation={0}
                    sx={{ 
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      '&:before': { display: 'none' }
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isNetworkSelected(network.id)}
                              onChange={() => handleNetworkToggle(network)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          }
                          label={
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <NetworkIcon color="primary" />
                              <Typography variant="subtitle2" fontWeight={600}>{network.name}</Typography>
                              <Chip 
                                label={`${getNetworkSectionCount(network.id)} sections`}
                                size="small"
                                variant="outlined"
                              />
                              {isNetworkSelected(network.id) && (
                                <CheckIcon color="success" fontSize="small" />
                              )}
                            </Stack>
                          }
                          onClick={(e) => e.stopPropagation()}
                          sx={{ mr: 1 }}
                        />
                      </Stack>
                    </AccordionSummary>
                    
                    <AccordionDetails sx={{ pt: 0 }}>
                      <Box sx={{ pl: 4 }}>
                        {network.sections && network.sections.length > 0 ? (
                          <Stack spacing={1}>
                            <Typography variant="caption" color="text.secondary">
                              Sections in {network.name}:
                            </Typography>
                            {network.sections.map((section) => (
                              <FormControlLabel
                                key={section.id}
                                control={
                                  <Checkbox
                                    checked={isSectionSelected(section.id)}
                                    onChange={() => handleSectionToggle(section, network.id)}
                                    disabled={isNetworkSelected(network.id)}
                                    size="small"
                                  />
                                }
                                label={
                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    <SectionIcon color="secondary" fontSize="small" />
                                    <Typography variant="body2">{section.name}</Typography>
                                    {section.description && (
                                      <Typography variant="caption" color="text.secondary">
                                        ({section.description})
                                      </Typography>
                                    )}
                                  </Stack>
                                }
                                sx={{ display: 'flex' }}
                              />
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No sections available in this network
                          </Typography>
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Stack>
            </Box>
          </Paper>
        </Grid>

        {/* Right Panel - Selected Permissions */}
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 3, minHeight: 300 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Selected Permissions ({selectedPermissions.length})
            </Typography>
            
            {selectedPermissions.length === 0 ? (
              <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  No permissions selected
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Select networks or sections from the left panel
                </Typography>
              </Stack>
            ) : (
              <List dense>
                {selectedPermissions.map((permission, index) => (
                  <React.Fragment key={`${permission.type}-${permission.id}`}>
                    <ListItem sx={{ px: 0 }}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mr: 1 }}>
                        {permission.type === 'network' ? (
                          <NetworkIcon color="primary" fontSize="small" />
                        ) : (
                          <SectionIcon color="secondary" fontSize="small" />
                        )}
                      </Stack>
                      <ListItemText
                        primary={
                          <Typography variant="body2" fontWeight={500}>
                            {permission.name}
                          </Typography>
                        }
                        secondary={
                          <Chip
                            label={permission.type === 'network' ? 'Network' : 'Section'}
                            size="small"
                            color={permission.type === 'network' ? 'primary' : 'secondary'}
                            variant="outlined"
                          />
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          size="small"
                          onClick={() => removePermission(permission)}
                        >
                          <RemoveIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                    {index < selectedPermissions.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>

      {error && (
        <FormHelperText error sx={{ mt: 2 }}>
          {error}
        </FormHelperText>
      )}

      {/* Summary */}
      {selectedPermissions.length > 0 && (
        <Alert severity="success">
          <Typography variant="body2">
            <strong>Summary:</strong> User will have access to{' '}
            {selectedPermissions.filter(p => p.type === 'network').length} network(s) and{' '}
            {selectedPermissions.filter(p => p.type === 'section').length} individual section(s).
          </Typography>
        </Alert>
      )}
    </Stack>
  );
};

export default NetworkSectionSelector; 